// VerdictBanner — prominent verdict banner shown between toolbar and canvas
// after simulation ends. Displays PASS / FAIL / ERROR (aborted) / NO ASSERTIONS,
// assertion breakdown, BDD narrative, and cumulative coverage stats.
//
// Truthfulness rules:
// - An aborted run (status 'failed') is an ERROR, never a green PASS.
// - A completed run with zero assertions is NO ASSERTIONS (inconclusive),
//   never silence and never a PASS.

import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSimulationStatus,
  selectEffectiveAssertionSummary,
  selectScenarioVerdict,
  selectCoverageSummary,
  selectActualPath,
} from '../../store/slices/simulationSlice.ts';
import {
  selectGiven,
  selectWhen,
  selectThen,
  selectTotalEvents,
} from '../../store/slices/eventsSlice.ts';
import { deriveEffectiveVerdict } from './scenarioBadges.ts';

const TIER_LABELS: Record<string, string> = {
  path: 'Path',
  boundary_crossing: 'Boundary',
  schema_contract: 'Schema',
  branch_evaluation: 'Branch',
  terminal_state: 'Terminal',
};

/** Feature-detect the store's upcoming `skipped` count on the summary. */
function skippedCount(summary: unknown): number {
  if (summary && typeof summary === 'object' && 'skipped' in summary) {
    const v = (summary as { skipped?: unknown }).skipped;
    if (typeof v === 'number' && v > 0) return v;
  }
  return 0;
}

export default function VerdictBanner() {
  const status = useSelector(selectSimulationStatus);
  const summary = useSelector(selectEffectiveAssertionSummary);
  const storeVerdict = useSelector(selectScenarioVerdict);
  const coverage = useSelector(selectCoverageSummary);
  const actualPath = useSelector(selectActualPath);
  const totalEvents = useSelector(selectTotalEvents);
  const given = useSelector(selectGiven);
  const when = useSelector(selectWhen);
  const then_ = useSelector(selectThen);

  const [expanded, setExpanded] = useState(false);

  if (status !== 'completed' && status !== 'failed') return null;

  const verdict = deriveEffectiveVerdict(status, summary, storeVerdict);
  if (verdict === null) return null;

  const skipped = skippedCount(summary);
  const hasBdd = given || when || then_;

  // ── Aborted run: distinct error banner, never a pass/fail verdict ──
  if (verdict === 'error') {
    const processed = Math.min(actualPath.length, totalEvents);
    return (
      <div className="verdict-banner verdict-error" role="alert" aria-label="Simulation aborted">
        <div className="verdict-main">
          <span className="verdict-icon">⚠</span>
          <span className="verdict-label">ERROR</span>
          <span className="verdict-counts">
            Simulation aborted — {processed} of {totalEvents} events processed
            {summary.total > 0 && (
              <>
                {' '}
                · {summary.total} assertions before abort: {summary.passed} passed
                {summary.failed > 0 && (
                  <>
                    , <strong>{summary.failed} failed</strong>
                  </>
                )}
              </>
            )}
          </span>
        </div>
      </div>
    );
  }

  // ── Completed with zero assertions: inconclusive, not silent ──
  if (verdict === 'none') {
    return (
      <div
        className="verdict-banner verdict-none"
        role="status"
        aria-label="Scenario completed with no assertions"
      >
        <div className="verdict-main">
          <span className="verdict-icon">○</span>
          <span className="verdict-label">NO ASSERTIONS</span>
          <span className="verdict-counts">
            Run completed but no assertions were evaluated — result is inconclusive
            {skipped > 0 && <> ({skipped} skipped)</>}
          </span>
        </div>
      </div>
    );
  }

  const isPassing = verdict === 'pass';

  return (
    <div
      className={`verdict-banner ${isPassing ? 'verdict-pass' : 'verdict-fail'}`}
      role="status"
      aria-label={`Scenario ${isPassing ? 'passed' : 'failed'}`}
    >
      <div className="verdict-main">
        <span className="verdict-icon">{isPassing ? '✓' : '✗'}</span>
        <span className="verdict-label">{isPassing ? 'PASS' : 'FAIL'}</span>
        <span className="verdict-counts">
          {summary.total} assertions: {summary.passed} passed
          {summary.failed > 0 && (
            <>
              , <strong>{summary.failed} failed</strong>
            </>
          )}
          {skipped > 0 && <>, {skipped} skipped</>}
        </span>

        {coverage && coverage.totalNodes > 0 && (
          <span className="verdict-coverage">
            {coverage.coveredNodes}/{coverage.totalNodes} nodes
            {coverage.totalBranches > 0 && (
              <>
                {' '}
                · {coverage.coveredBranches}/{coverage.totalBranches} branches
              </>
            )}
          </span>
        )}

        {(hasBdd || Object.keys(summary.byType).length > 1) && (
          <button
            className="verdict-expand-btn"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? '▲' : '▼'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="verdict-details">
          {/* Per-tier breakdown */}
          {Object.keys(summary.byType).length > 1 && (
            <div className="verdict-tiers">
              {Object.entries(summary.byType).map(([tier, counts]) => {
                const tierSkipped = skippedCount(counts);
                return (
                  <span
                    key={tier}
                    className={`verdict-tier ${counts.failed > 0 ? 'verdict-tier-fail' : 'verdict-tier-pass'}`}
                  >
                    {TIER_LABELS[tier] ?? tier}: {counts.passed}✓
                    {counts.failed > 0 && <> {counts.failed}✗</>}
                    {tierSkipped > 0 && <> {tierSkipped} skipped</>}
                  </span>
                );
              })}
            </div>
          )}

          {/* BDD narrative */}
          {hasBdd && (
            <div className="verdict-bdd">
              {given && (
                <span className="bdd-clause">
                  <strong>Given</strong> {given}
                </span>
              )}
              {when && (
                <span className="bdd-clause">
                  <strong>When</strong> {when}
                </span>
              )}
              {then_ && (
                <span className="bdd-clause">
                  <strong>Then</strong> {then_}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
